import React from "react";
import { Box, Typography, Avatar } from "@mui/material";
import { StaffUser } from "../../types/staff";
import { buildStaffTree, StaffTreeNode } from "../../utils/staffTree";
import { humanizeEnum } from "../../utils/enumLabels";
import { avatarFor } from "../../utils/personAvatar";
import { rankHierarchy, fieldTitleFor, RankedPerson } from "../../utils/staffRank";

interface Props {
    staff: StaffUser[];
    onSelect: (id: number) => void;
}

const LINE = '#cfd6dd';
const DROP = 20;      // height of every vertical connector
const CARD_W = 148;

/** Title follows the TREE, not the role column: an agent with a team is a Field Manager/Director. */
const titleOf = (person: StaffUser, r: RankedPerson): string =>
    (person.role === 'FIELD_AGENT' && fieldTitleFor(r.rank)) || humanizeEnum(person.role);

const Drop = () => <Box sx={{ width: '2px', height: `${DROP}px`, bgcolor: LINE }} />;

const PersonCard: React.FC<{
    node: StaffTreeNode<StaffUser>;
    onSelect: (id: number) => void;
    ranked: RankedPerson;
}> = ({ node, onSelect, ranked }) => {
    const { staff: person } = node;
    return (
        // A native title rather than MUI's Tooltip: one per person is a lot of listeners for no
        // gain, and it kept the chart cheap enough to render inside tests.
        <Box title={person.email || person.phoneNumber || ''}>
            <Box
                onClick={() => onSelect(person.id)}
                sx={{
                    width: CARD_W, px: 1, py: 1.25, cursor: 'pointer',
                    borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25,
                    transition: 'box-shadow .15s, border-color .15s',
                    '&:hover': { boxShadow: 3, borderColor: 'primary.main' },
                }}
            >
                <Avatar
                    src={avatarFor()}
                    sx={{
                        width: 54, height: 54, mb: 0.5, bgcolor: 'grey.100',
                        border: '3px solid',
                        borderColor: person.status === 'ACTIVE' ? 'primary.light' : 'error.light',
                    }}
                />
                <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: CARD_W - 16 }}>
                    {person.firstName} {person.lastName || ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: CARD_W - 16 }}>
                    {titleOf(person, ranked)}
                </Typography>
                {ranked.rank && (
                    <Typography variant="caption" color="primary" fontWeight={600}>
                        ({ranked.rank}) · {ranked.directReports}
                    </Typography>
                )}
                {person.status !== 'ACTIVE' && (
                    <Typography variant="caption" color="error" fontWeight={600}>
                        {humanizeEnum(person.status)}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

/**
 * One node and everything under it, drawn top-down.
 *
 * The connectors are real elements rather than ::before/::after tricks — order is then obvious and
 * identical to the ManagerApp's chart: a drop OUT of the parent, then the sibling bar, then a drop
 * INTO each child. Each child paints two half-width bars meeting at its centre; the first hides its
 * left half and the last its right, so a row reads as one line with square ends.
 */
const ChartNode: React.FC<{
    node: StaffTreeNode<StaffUser>;
    onSelect: (id: number) => void;
    ranks: Map<number, RankedPerson>;
}> = ({ node, onSelect, ranks }) => {
    const { children } = node;
    const ranked = ranks.get(node.staff.id) ?? { directReports: 0, managerReports: 0, rank: null };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <PersonCard node={node} onSelect={onSelect} ranked={ranked} />

            {children.length > 0 && (
                <>
                    <Drop />
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        {children.map((child, i) => {
                            const first = i === 0;
                            const last = i === children.length - 1;
                            const only = children.length === 1;
                            return (
                                <Box
                                    key={child.staff.id}
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 1 }}
                                >
                                    {!only && (
                                        // mx:-1 cancels this column's px:1 so the bar reaches the
                                        // neighbour's — without it every join shows a gap.
                                        <Box sx={{ display: 'flex', alignSelf: 'stretch', mx: -1 }}>
                                            <Box sx={{ flex: 1, height: '2px', bgcolor: first ? 'transparent' : LINE }} />
                                            <Box sx={{ flex: 1, height: '2px', bgcolor: last ? 'transparent' : LINE }} />
                                        </Box>
                                    )}
                                    <Drop />
                                    <ChartNode node={child} onSelect={onSelect} ranks={ranks} />
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </Box>
    );
};

/**
 * The whole org as a top-down chart, centred on the page. Anyone holding reports is marked
 * "(Manager)" with their direct count, so a glance up the chart answers who someone reports to.
 * Scrolls sideways once the org is wider than the page rather than squashing.
 */
const StaffReportingTree: React.FC<Props> = ({ staff, onSelect }) => {
    const roots = buildStaffTree(staff);
    // Ranked over the WHOLE list, not per-branch: a Director deep in one leg lifts everyone above.
    const ranks = rankHierarchy(staff);

    if (staff.length === 0) {
        return <Typography variant="body2" color="text.secondary">No staff loaded.</Typography>;
    }

    return (
        <Box sx={{ overflowX: 'auto', pb: 2 }} data-testid="staff-tree">
            {/* max-content + auto margins: centred while it fits, scrollable once it does not. */}
            <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start', width: 'max-content', mx: 'auto' }}>
                {roots.map((root) => (
                    <ChartNode key={root.staff.id} node={root} onSelect={onSelect} ranks={ranks} />
                ))}
            </Box>
        </Box>
    );
};

export default StaffReportingTree;
